import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Page state that survives leaving the page and coming back.
 *
 * A tab, filter or page number kept in useState is lost the moment someone
 * opens a task or a profile: Back rebuilds the page from its defaults. These
 * keep the value in the address instead (`/employee/12?tab=tasks&page=2`), so
 * Back, a refresh and a copied link all land where the person was.
 *
 *   const [tab, setTab] = useUrlState('tab', 'details');
 *   const [page, setPage] = useUrlState('page', 1);          // numbers stay numbers
 *   const [mine, setMine] = useUrlState('mine', false);      // true/false as 1/0
 *   const [filters, setFilters] = useUrlFilters({ status: 'All', from: '' }); // one key each
 *
 * The default is left out of the address, so an untouched page keeps a clean
 * link. Writes replace the current history entry rather than adding one, so
 * Back leaves the page instead of stepping through every click. Keys must be
 * unique on the page.
 */

const isNumber = (v) => typeof v === 'number';
const isBool = (v) => typeof v === 'boolean';

const encode = (value, initial) => {
    if (value === undefined || value === null || value === '') return null;
    if (isBool(initial)) return value ? '1' : null;
    return String(value);
};

const decode = (raw, initial) => {
    if (raw === null) return initial;
    if (isNumber(initial)) {
        const n = Number(raw);
        return Number.isFinite(n) ? n : initial;
    }
    if (isBool(initial)) return raw === '1' || raw === 'true';
    return raw;
};

// Several keys changed in one click (a filter and the page number) must build
// on each other, but the router only shows the new address on its next render.
// So remember the last write and which location it was made from: until the
// router moves past that location, the next write starts from it.
let lastWrite = { from: null, search: '' };

const useWriteParams = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const here = useRef(location);
    here.current = location;
    return useCallback((patch) => {
        const loc = here.current;
        const base = lastWrite.from === loc.key ? lastWrite.search : loc.search;
        const params = new URLSearchParams(base);
        Object.entries(patch).forEach(([key, raw]) => {
            if (raw === null) params.delete(key);
            else params.set(key, raw);
        });
        const search = params.toString() ? `?${params.toString()}` : '';
        if (search === base) return;
        lastWrite = { from: loc.key, search };
        navigate({ pathname: loc.pathname, search, hash: loc.hash }, { replace: true, state: loc.state });
    }, [navigate]);
};

export function useUrlState(key, initial) {
    const location = useLocation();
    const write = useWriteParams();
    const read = (search) => decode(new URLSearchParams(search).get(key), initial);

    const [value, setValue] = useState(() => read(location.search));
    const current = useRef(value);
    current.current = value;

    // The address moved on its own (Back/Forward inside the page, a link to
    // the same page with other settings): follow it.
    useEffect(() => {
        const next = read(location.search);
        if (next !== current.current) setValue(next);
    }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = useCallback((next) => {
        const v = typeof next === 'function' ? next(current.current) : next;
        current.current = v;
        setValue(v);
        const raw = encode(v, initial);
        write({ [key]: raw === encode(initial, initial) ? null : raw });
    }, [key, write]); // eslint-disable-line react-hooks/exhaustive-deps

    return [value, set];
}

/** A filters object, each field its own key in the address (with an optional prefix). */
export function useUrlFilters(defaults, prefix = '') {
    const location = useLocation();
    const write = useWriteParams();
    const keys = Object.keys(defaults);
    const read = (search) => {
        const params = new URLSearchParams(search);
        const out = {};
        keys.forEach((k) => { out[k] = decode(params.get(prefix + k), defaults[k]); });
        return out;
    };
    const same = (a, b) => keys.every((k) => a[k] === b[k]);

    const [value, setValue] = useState(() => read(location.search));
    const current = useRef(value);
    current.current = value;

    useEffect(() => {
        const next = read(location.search);
        if (!same(next, current.current)) setValue(next);
    }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = useCallback((next) => {
        const v = { ...defaults, ...(typeof next === 'function' ? next(current.current) : next) };
        current.current = v;
        setValue(v);
        const patch = {};
        keys.forEach((k) => {
            const raw = encode(v[k], defaults[k]);
            patch[prefix + k] = raw === encode(defaults[k], defaults[k]) ? null : raw;
        });
        write(patch);
    }, [write]); // eslint-disable-line react-hooks/exhaustive-deps

    return [value, set];
}

/**
 * Run `fn` when one of `deps` changes, never on the first render.
 *
 * For "a filter changed, go back to page 1": done as a plain effect it also
 * runs when the page opens, throwing away the page number Back just restored.
 * Compares values, so React's development double-run is not a change either.
 */
export function useOnChange(fn, deps) {
    const previous = useRef(deps);
    useEffect(() => {
        const changed = deps.some((d, i) => !Object.is(d, previous.current[i]));
        previous.current = deps;
        if (changed) fn();
    }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Back to wherever the person came from - the profile tab, the filtered list,
 * the project - and only to `fallback` when there is nowhere to go back to (a
 * link opened in a new tab). For Back and Cancel buttons and after a save.
 */
export function useGoBack(fallback) {
    const navigate = useNavigate();
    return useCallback(() => {
        if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
        else navigate(fallback, { replace: true });
    }, [navigate, fallback]);
}
